import React, { useEffect, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import useReviews from '../../hooks/useReviews';
import ReviewForm from './ReviewForm';
import ReviewList from './ReviewList';

const ReviewSection = ({ productId }) => {
  const [showForm, setShowForm] = useState(false);
  const {
    reviews,
    loading,
    error,
    addReview,
    getAverageRating,
    getRatingDistribution,
  } = useReviews(productId);

  // ======================
  // DATE FORMATTER HELPER  
  // ======================
  const formatDate = (dateString) => {
    if (!dateString) return 'Date not available';
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return 'Invalid date';
      }
      
      // Format: December 30, 2024
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date parsing error:', error, dateString);
      return 'Date error';
    }
  };

  // Handle review submission
  const handleSubmitReview = async (reviewData) => {
    const result = await addReview(reviewData);
    
    if (result.success) {
      setShowForm(false);
      // Show success message
      alert('Thank you for your review!');
    } else {
      // Show error message
      alert(result.error || 'Failed to submit review');
    }
    
    return result;
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-semibold mb-2">Failed to load reviews</p>
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-8 h-8 text-primary-600" />
          <h2 className="text-3xl font-display font-bold text-dark-900">
            Customer Reviews
          </h2>
        </div>
        
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Write a Review
          </button>
        )}
      </div>

      {/* Review Form */}
      {showForm && (
        <ReviewForm
          productId={productId}
          onSubmit={handleSubmitReview}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Review List - Pass formatDate function as prop */}
      <ReviewList
        reviews={reviews}
        averageRating={getAverageRating()}
        distribution={getRatingDistribution()}
        formatDate={formatDate}
      />
    </div>
  );
};

export default ReviewSection;